const CELL_SIZE = 1.25
const WORLD_MIN = -33
const WORLD_MAX = 33
const GRID_SIZE = Math.round((WORLD_MAX - WORLD_MIN) / CELL_SIZE) + 1

const keyFor = (_x, _y) => `${_x},${_y}`

const worldToCell = (_value) => ({
    x: Math.max(0, Math.min(GRID_SIZE - 1, Math.round((_value.x - WORLD_MIN) / CELL_SIZE))),
    y: Math.max(0, Math.min(GRID_SIZE - 1, Math.round((_value.y - WORLD_MIN) / CELL_SIZE)))
})

const cellToWorld = (_cell) => ({
    x: WORLD_MIN + _cell.x * CELL_SIZE,
    y: WORLD_MIN + _cell.y * CELL_SIZE
})

const nearestOpen = (_cell, _isBlocked) =>
{
    if(!_isBlocked(_cell.x, _cell.y))
        return _cell
    for(let radius = 1; radius <= 5; radius++)
    {
        for(let x = _cell.x - radius; x <= _cell.x + radius; x++)
        {
            for(let y = _cell.y - radius; y <= _cell.y + radius; y++)
            {
                if(x >= 0 && y >= 0 && x < GRID_SIZE && y < GRID_SIZE && !_isBlocked(x, y))
                    return { x, y }
            }
        }
    }
    return null
}

const lineIsClear = (_start, _end, _isBlocked) =>
{
    const distance = Math.hypot(_end.x - _start.x, _end.y - _start.y)
    const steps = Math.max(1, Math.ceil(distance / (CELL_SIZE * 0.45)))
    for(let index = 1; index < steps; index++)
    {
        const progress = index / steps
        const cell = worldToCell({
            x: _start.x + (_end.x - _start.x) * progress,
            y: _start.y + (_end.y - _start.y) * progress
        })
        if(_isBlocked(cell.x, cell.y))
            return false
    }
    return true
}

export const findPath = (_start, _end, _obstacles = []) =>
{
    const isBlocked = (_x, _y) =>
    {
        if(_x < 0 || _y < 0 || _x >= GRID_SIZE || _y >= GRID_SIZE)
            return true
        const point = cellToWorld({ x: _x, y: _y })
        if(Math.hypot(point.x, point.y) > 33)
            return true
        return _obstacles.some((_obstacle) => Math.hypot(point.x - _obstacle.x, point.y - _obstacle.y) < _obstacle.radius)
    }

    const start = nearestOpen(worldToCell(_start), isBlocked)
    const end = nearestOpen(worldToCell(_end), isBlocked)
    if(!start || !end)
        return []

    const startKey = keyFor(start.x, start.y)
    const endKey = keyFor(end.x, end.y)
    if(startKey === endKey)
        return [{ x: _end.x, y: _end.y }]

    const open = [{ ...start, score: 0 }]
    const cameFrom = new Map()
    const costs = new Map([[startKey, 0]])
    const closed = new Set()
    const directions = [
        [-1, 0, 1], [1, 0, 1], [0, -1, 1], [0, 1, 1],
        [-1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [1, 1, Math.SQRT2]
    ]

    while(open.length)
    {
        open.sort((_a, _b) => _a.score - _b.score)
        const current = open.shift()
        const currentKey = keyFor(current.x, current.y)
        if(closed.has(currentKey))
            continue
        if(currentKey === endKey)
        {
            const path = [{ x: _end.x, y: _end.y }]
            let cursor = currentKey
            while(cursor !== startKey)
            {
                const [x, y] = cursor.split(',').map(Number)
                path.unshift(cellToWorld({ x, y }))
                cursor = cameFrom.get(cursor)
            }
            const smoothed = []
            let anchor = { x: _start.x, y: _start.y }
            let index = 0
            while(index < path.length)
            {
                let furthest = index
                for(let candidate = path.length - 1; candidate >= index; candidate--)
                {
                    if(lineIsClear(anchor, path[candidate], isBlocked))
                    {
                        furthest = candidate
                        break
                    }
                }
                smoothed.push(path[furthest])
                anchor = path[furthest]
                index = furthest + 1
            }
            return smoothed
        }

        closed.add(currentKey)
        const currentCost = costs.get(currentKey) ?? Infinity
        for(const [offsetX, offsetY, stepCost] of directions)
        {
            const x = current.x + offsetX
            const y = current.y + offsetY
            const key = keyFor(x, y)
            if(closed.has(key) || isBlocked(x, y))
                continue
            const cost = currentCost + stepCost
            if(cost >= (costs.get(key) ?? Infinity))
                continue
            costs.set(key, cost)
            cameFrom.set(key, currentKey)
            const heuristic = Math.hypot(end.x - x, end.y - y)
            open.push({ x, y, score: cost + heuristic })
        }
    }
    return []
}
